/* eslint-disable jsx-a11y/media-has-caption */
import { makeStyles, Theme, useTheme } from '@material-ui/core';
import L, { DivIcon } from 'leaflet';
import React, { useCallback, useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import * as ReactDOMServer from 'react-dom/server';
import clsx from 'clsx';
import { capitalizeAll } from '@moderepo/mode-app-components';
import { EntityMetricsDefinition, NumericMetricInfo } from '@moderepo/mode-internal-apis';
import moment from 'moment';
import { WarningIcon } from './WarningIcon';
import { InfoIcon } from './InfoIcon';
import { useSelectProject } from '../../../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../../../utils/customHooks';
import { EventMarkerIconSetting, EventMetricsNameMapping, MOBILITY_DEFAULT_EVENT_LATITUDE_METRIC_NAME, MOBILITY_DEFAULT_EVENT_LONGITUDE_METRIC_NAME,
    MOBILITY_DEFAULT_EVENT_SPEED_METRIC_NAME, MOBILITY_DEFAULT_EVENT_TYPE_METRIC_NAME, RouteVehicleEvent, TripData } from '.';
import { ValueToEventStateMapping } from '../../utils';
import { EventMarkerIcon } from './utils';
import { EventState } from '../..';
import { getDateTimeDisplay } from '../../../../containerUtils';
import { VideosScroller } from '../../../../misc';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            background: 'none',
            border    : 'none',
            height    : 'fit-content',
        },

        icon: {
            transition: 'transform 200ms linear',
        },

        markerPopup: {
            '& a.leaflet-popup-close-button': {
                color: theme.palette.text.primary,
            },

            '& .leaflet-popup-content-wrapper': {
                overflow: 'hidden',
                padding : 0,
            },

            '& .leaflet-popup-content': {
                padding: 0,
                margin : 0,
            },
        },

        markerPopupContent: {
            minWidth: 250,
        },

        markerPopupHeader: {
            padding      : theme.spacing(1, 3),
            width        : '100%',
            display      : 'inline-block',
            textAlign    : 'center',
            fontWeight   : 'bold',
            background   : theme.palette.success.main,
            color        : theme.palette.success.contrastText,
            textTransform: 'capitalize',

            '& .event-name': {
                fontSize     : 'large',
                paddingBottom: theme.spacing(0.5),
            },

            '& .event-date': {
            },

            [`&.${EventState.NEUTRAL}`]: {
                background: theme.palette.info.main,
                color     : theme.palette.info.contrastText,
            },
            [`&.${EventState.GOOD}`]: {
                background: theme.palette.success.main,
                color     : theme.palette.success.contrastText,
            },
            [`&.${EventState.SERIOUS}`]: {
                background: theme.palette.warning.main,
                color     : theme.palette.warning.contrastText,
            },
            [`&.${EventState.CRITICAL}`]: {
                background: theme.palette.error.main,
                color     : theme.palette.error.contrastText,
            },
        },

        markerPopupBody: {
            padding: theme.spacing(1),
        },

        markerPopupInfo: {
            display      : 'flex',
            flexDirection: 'row',
            margin       : theme.spacing(0.5, 0),
            '& .label'   : {
                fontWeight: 'bold',
            },
            '& .value': {
                marginLeft: theme.spacing(1),
            },
        },

        eventVideosScroller: {
            width    : 350,
            marginTop: theme.spacing(1),
        },
    };
}, { name: 'EventMarker', index: 1 });


const DEFAULT_ICON_SIZE_IN_PIXEL = 40;



export interface EventMarkerProps {
    readonly projectId: number;
    readonly latitude: number;
    readonly longitude: number;

    // The size of the icon. The default value is 1 for 1x. To double the icon size, use value 2. The value must be between 1 and 5 and will
    // automatically be set to 1 to 5 if it goes over the bounds.
    readonly size?: number | undefined;

    readonly eventMetricsDefinition: EntityMetricsDefinition;
    readonly tripData: TripData;
    readonly routeEvent: RouteVehicleEvent;

    readonly eventMetricsNameMapping?: EventMetricsNameMapping | undefined;
    readonly eventTypeToEventStateMapping?: ValueToEventStateMapping | undefined;
    readonly eventMarkerIconsSettings?: EventMarkerIconSetting | undefined;
    readonly eventMarkerIconsSettingsByEventType?: {
        readonly [eventType: string]: EventMarkerIconSetting | undefined;
    } | undefined;
}


/**
 * This is a custom Marker component that displays an image of a warning icon as marker icon.
 */
export const EventMarker: React.FC<EventMarkerProps> = (props: EventMarkerProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const theme = useTheme();


    /**
     * Each event can have a state based on their "eventType" value. For example, breathalyzer's eventType value "0" means passed result
     * therefore the eventState for value "0" is GOOD. The value "1" means failure result therefore the eventState for value "1" is "CRITICAL".
     * Therefore, we need to convert the event.eventType value to EventState based on the "eventTypeToEventStateMapping". This state will be
     * used for highlighting the marker popup. If a "eventTypeToEventStateMapping" is not specified or there is no configuration for the
     * "routeEvent.eventType" then a "INFO" state will be used by default.
     */
    const eventState = useMemo(() => {
        return props.eventTypeToEventStateMapping?.[props.routeEvent.eventType] ?? EventState.NEUTRAL;
    }, [props.eventTypeToEventStateMapping, props.routeEvent.eventType]);



    // Validate the icon's size prop and make sure it is between 1 and 5.
    const iconSize = useMemo(() => {
        if (props.size !== undefined) {
            return Math.max(1, Math.min(5, props.size));
        }
        return 1;
    }, [props.size]);


    /**
     * Get the icon's default color based on the event's state. We will use this to color the icon if the icon don't have custom settings.
     */
    const defaultIconColorsByEventState = useMemo((): {
        readonly background: string | undefined;
        readonly foreground: string | undefined;
    }| undefined => {
        switch (eventState) {
            case EventState.NEUTRAL:
                return {
                    background: theme.palette.info.main,
                    foreground: theme.palette.info.contrastText,
                };
            case EventState.GOOD:
                return {
                    background: theme.palette.success.main,
                    foreground: theme.palette.success.contrastText,
                };
            case EventState.SERIOUS:
                return {
                    background: theme.palette.warning.main,
                    foreground: theme.palette.warning.contrastText,
                };
            case EventState.CRITICAL:
                return {
                    background: theme.palette.error.main,
                    foreground: theme.palette.error.contrastText,
                };
            default:
                return undefined;
        }
    }, [eventState, theme.palette.error.contrastText, theme.palette.error.main, theme.palette.info.contrastText, theme.palette.info.main,
        theme.palette.success.contrastText, theme.palette.success.main, theme.palette.warning.contrastText, theme.palette.warning.main]);


    /**
     * Create and return an Icon image to be used as the marker icon based on the event's state. An event can have different state e.g. Info,
     * Good, Warning, Serious, Critical, etc... and this function will return a different icon for different states. The event's type will
     * determines the event's state.
     */
    const getEventIcon = useCallback((): React.ReactElement => {

        const icon = props.eventMarkerIconsSettingsByEventType?.[props.routeEvent.eventType]?.icon ?? props.eventMarkerIconsSettings?.icon;
        const background = defaultIconColorsByEventState?.background
                              ?? props.eventMarkerIconsSettingsByEventType?.[props.routeEvent.eventType]?.background
                              ?? props.eventMarkerIconsSettings?.background;
        const foreground = defaultIconColorsByEventState?.foreground
                              ?? props.eventMarkerIconsSettingsByEventType?.[props.routeEvent.eventType]?.foreground
                              ?? props.eventMarkerIconsSettings?.foreground;

        switch (icon) {
            case EventMarkerIcon.INFO:
                return (
                    <InfoIcon
                        className={classes.icon}
                        background={background}
                        foreground={foreground}
                    />
                );
            default:
                return (
                    <WarningIcon
                        className={classes.icon}
                        background={background}
                        foreground={foreground}
                    />
                );
        }
    }, [classes.icon, defaultIconColorsByEventState?.background, defaultIconColorsByEventState?.foreground,
        props.eventMarkerIconsSettings?.background, props.eventMarkerIconsSettings?.foreground, props.eventMarkerIconsSettings?.icon,
        props.eventMarkerIconsSettingsByEventType, props.routeEvent.eventType]);


    /**
     * Create and return the Marker icon component
     */
    const icon = useMemo((): DivIcon => {
        return L.divIcon({
            className : classes.root,
            iconSize  : [DEFAULT_ICON_SIZE_IN_PIXEL * iconSize, DEFAULT_ICON_SIZE_IN_PIXEL * iconSize],
            iconAnchor: [(DEFAULT_ICON_SIZE_IN_PIXEL * iconSize) / 2, (DEFAULT_ICON_SIZE_IN_PIXEL * iconSize) / 2],
            html      : ReactDOMServer.renderToString(getEventIcon()),
        });
    }, [getEventIcon, classes.root, iconSize]);



    /**
     * Get the display name of the eventType's value. Event types' values are usually not user-friendly values e.g. "0", "1", "2001", etc...
     * These values usually have display value which are user friendly and they are defined in the eventMetricsDefinition.enumDefinitions.
     * This function will try to find the enumDefinition for the eventType's metric and value and return the displayName if there is one.
     * If not, it will return the eventType's value as is.
     */
    const eventTypeDisplayName = useMemo(() => {

        // Find the tag metric definition from the eventMetricsDefinition.'s tagMetrics that is used for the eventType. Because different project
        // might use different metric name, we need to use the "eventMetricsNameMapping" when we look for the metric definition for the eventType.
        const eventTypeTagMetricInfo = props.eventMetricsDefinition.tagMetrics?.find((metricInfo) => {
            return metricInfo.name === (props.eventMetricsNameMapping?.eventType ?? MOBILITY_DEFAULT_EVENT_TYPE_METRIC_NAME);
        });

        // Once we have the tag metric info for the event type metric, we need to find the enum definition from the "enumDefinitions"
        // to see if there is a definition for the routeEvent.eventType. If there is, return it's name which will be the event's display name
        const enumDef = eventTypeTagMetricInfo?.enumDefinitions?.find((enumDef) => {
            return enumDef.value === props.routeEvent.eventType;
        });

        if (enumDef) {
            return enumDef.name;
        }

        return props.routeEvent.eventType;
    }, [props.eventMetricsDefinition.tagMetrics, props.eventMetricsNameMapping?.eventType, props.routeEvent.eventType]);



    /**
     * Get a map of the metrics definition for each of the event's numeric metrics e.g. lon, lat, speed, etc.... We will need to display
     * their displayName and unit.
     *
     * TODO - Make this configurable to support other events that don't have lat, lon, speed but have other metrics instead.
     */
    const eventMetricToMetricsDefinitionMap = useMemo(() => {
        const initialResult = {
            lat  : undefined,
            lon  : undefined,
            speed: undefined,
        };
        return props.eventMetricsDefinition.numericMetrics?.reduce((result: {
            readonly lat: NumericMetricInfo | undefined;
            readonly lon: NumericMetricInfo | undefined;
            readonly speed: NumericMetricInfo | undefined;
        }, metricInfo) => {
            if (metricInfo.name === (props.eventMetricsNameMapping?.lat ?? MOBILITY_DEFAULT_EVENT_LATITUDE_METRIC_NAME)) {
                // If the metric def object is for LATITUDE, add it to the result object under "lat" attribute
                return {
                    ...result,
                    lat: metricInfo,
                };
            }
            if (metricInfo.name === (props.eventMetricsNameMapping?.lon ?? MOBILITY_DEFAULT_EVENT_LONGITUDE_METRIC_NAME)) {
                // If the metric def object is for LONGITUDE, add it to the result object under "lat" attribute
                return {
                    ...result,
                    lon: metricInfo,
                };
            }
            if (metricInfo.name === (props.eventMetricsNameMapping?.speed ?? MOBILITY_DEFAULT_EVENT_SPEED_METRIC_NAME)) {
                // If the metric def object is for SPEED, add it to the result object under "lat" attribute
                return {
                    ...result,
                    speed: metricInfo,
                };
            }
            return result;
        }, initialResult) ?? initialResult;
    }, [props.eventMetricsDefinition.numericMetrics, props.eventMetricsNameMapping?.lat, props.eventMetricsNameMapping?.lon,
        props.eventMetricsNameMapping?.speed]);



    return (
        <Marker
            position={[props.latitude, props.longitude]}
            icon={icon}
        >
            <Popup
                className={classes.markerPopup}
                minWidth={undefined}
            >
                <div className={classes.markerPopupContent}>
                    <span className={clsx(classes.markerPopupHeader, eventState)}>
                        <div className="event-name">
                            {eventTypeDisplayName}
                        </div>
                        <div className="event-date">
                            {getDateTimeDisplay(moment(props.routeEvent.timestamp).toISOString(), trans)}
                        </div>
                    </span>
                    <div className={classes.markerPopupBody}>

                        {/*
                            Display important metrics' values. For now, the only value we have is speed.
                            TODO - Make this configurable to support other events that don't have speed.
                        */}
                        <div className={classes.markerPopupInfo}>
                            <span className="label">
                                {capitalizeAll(
                                    eventMetricToMetricsDefinitionMap.speed?.evaluator?.displayName
                                    ?? eventMetricToMetricsDefinitionMap.speed?.displayName
                                    ?? eventMetricToMetricsDefinitionMap.speed?.name
                                    ?? '',
                                )}:
                            </span>
                            <span className="value">
                                {trans('label.value_unit_display', {
                                    value: props.routeEvent.speed,
                                    unit : eventMetricToMetricsDefinitionMap.speed?.unit,
                                })}
                            </span>
                        </div>

                        {props.tripData.driver && (
                            <div className={classes.markerPopupInfo}>
                                <span className="label">
                                    {capitalizeAll(trans('mobility.label.driver'))}:
                                </span>
                                <span className="value">
                                    {props.tripData.driver.displaySettings.name}
                                </span>
                            </div>
                        )}

                        {props.tripData.vehicle && (
                            <div className={classes.markerPopupInfo}>
                                <span className="label">
                                    {capitalizeAll(trans('mobility.label.vehicle'))}:
                                </span>
                                <span className="value">
                                    {props.tripData.vehicle.displaySettings.name}
                                </span>
                            </div>
                        )}

                        {props.routeEvent.videos && props.routeEvent.videos.length > 0 && (
                            <VideosScroller
                                className={classes.eventVideosScroller}
                                videos={props.routeEvent.videos}
                            />
                        )}
                    </div>
                </div>
            </Popup>
        </Marker>
    );
};
